/**
 * PlaybookRulesEditor — a scope's rules: numbered list with Move up / Edit / Remove per row, inline
 * edit, and an add row. A rule that came from an improvement conversation carries a "From a
 * conversation" tag. All changes are local until that scope's "Save changes".
 *
 * `idPrefix` namespaces test ids and DOM ids so the all-properties group (`playbook`, the baseline
 * ids) and each property group (`playbook-property-<id>`) can sit on one page.
 */

import { useState } from 'react';
import { ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { PLAYBOOK_MAX_RULES, PLAYBOOK_TEXT_MAX } from '@resida/api-types/agent';
import type { DraftRule } from '../../../hooks/usePlaybookDraft';
import type { PlaybookAgentKind } from '@resida/api-types/agent';

const HELPER_TEXT: Record<PlaybookAgentKind, string> = {
  repair: 'Keep rules to how the agent should act. Property facts belong on the property record.',
  leasing: 'Prices, fees and amenities belong on the listing — agents only quote facts from there.',
};

const ICON_BUTTON = 'h-11 w-11 shrink-0 text-muted-foreground';

export interface PlaybookRulesEditorProps {
  agentKind: PlaybookAgentKind;
  /** Heading text: "Your rules" for all properties, "Rules for this property" in a property group. */
  heading?: string;
  idPrefix?: string;
  rules: DraftRule[];
  /** Validation message per rule index (from a 422). */
  errors: Record<number, string>;
  /** Validation message that names no single rule (e.g. too many rules). */
  listError?: string;
  onAdd: (text: string) => void;
  onEdit: (key: string, text: string) => void;
  onRemove: (key: string) => void;
  onMoveUp: (key: string) => void;
}

export function PlaybookRulesEditor({
  agentKind,
  heading = 'Your rules',
  idPrefix = 'playbook',
  rules,
  errors,
  listError,
  onAdd,
  onEdit,
  onRemove,
  onMoveUp,
}: PlaybookRulesEditorProps) {
  const [newText, setNewText] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const add = () => {
    if (newText.trim() === '') return;
    onAdd(newText);
    setNewText('');
  };

  return (
    <section
      aria-labelledby={`${idPrefix}-rules-heading`}
      className="flex flex-col gap-2"
      data-testid={`${idPrefix}-rules-section`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 id={`${idPrefix}-rules-heading`} className="text-base font-semibold text-foreground">
          {heading}
        </h3>
        <span className="text-sm text-muted-foreground" data-testid={`${idPrefix}-rule-count`}>
          {rules.length} of {PLAYBOOK_MAX_RULES}
        </span>
      </div>

      {listError && (
        <p role="alert" data-testid={`${idPrefix}-rules-error`} className="text-sm text-destructive">
          {listError}
        </p>
      )}

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${idPrefix}-rules-empty`}>
          No rules yet. Add one below, or turn off a default you don't want.
        </p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {rules.map((rule, i) => {
            const editing = editingKey === rule.key;
            const error = errors[i];
            return (
              <li key={rule.key} data-testid={`${idPrefix}-rule-${i}`} className="flex flex-col gap-1">
                {editing ? (
                  <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
                    <label htmlFor={`${idPrefix}-rule-input-${i}`} className="text-sm font-semibold text-foreground">
                      Edit rule {i + 1}
                    </label>
                    <Input
                      id={`${idPrefix}-rule-input-${i}`}
                      data-testid={`${idPrefix}-rule-input-${i}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="min-h-[44px]"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">
                        {editText.length} / {PLAYBOOK_TEXT_MAX}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setEditingKey(null)}
                          data-testid={`${idPrefix}-rule-cancel-${i}`}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="font-semibold"
                          disabled={editText.trim() === ''}
                          onClick={() => {
                            onEdit(rule.key, editText);
                            setEditingKey(null);
                          }}
                          data-testid={`${idPrefix}-rule-done-${i}`}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`flex items-center gap-1 rounded-lg border bg-card py-1 pl-3 pr-1 ${
                      error ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    <span className="w-6 shrink-0 tabular-nums text-sm text-muted-foreground">{i + 1}.</span>
                    <div className="flex min-w-0 flex-1 flex-col items-start gap-1 py-1.5">
                      <span className="break-words text-sm text-foreground">{rule.text}</span>
                      {rule.source === 'conversation' && (
                        <Badge variant="primary" data-testid={`${idPrefix}-rule-tag-${i}`}>
                          From a conversation
                        </Badge>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={ICON_BUTTON}
                      aria-label={`Move rule ${i + 1} up`}
                      disabled={i === 0}
                      onClick={() => onMoveUp(rule.key)}
                      data-testid={`${idPrefix}-rule-up-${i}`}
                    >
                      <ChevronUp aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={ICON_BUTTON}
                      aria-label={`Edit rule ${i + 1}`}
                      onClick={() => {
                        setEditingKey(rule.key);
                        setEditText(rule.text);
                      }}
                      data-testid={`${idPrefix}-rule-edit-${i}`}
                    >
                      <Pencil aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={ICON_BUTTON}
                      aria-label={`Remove rule ${i + 1}`}
                      onClick={() => onRemove(rule.key)}
                      data-testid={`${idPrefix}-rule-remove-${i}`}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                )}
                {error && (
                  <p role="alert" data-testid={`${idPrefix}-rule-error-${i}`} className="text-sm text-destructive">
                    {error}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <label htmlFor={`${idPrefix}-add-rule-input`} className="sr-only">
          Add a rule
        </label>
        <Input
          id={`${idPrefix}-add-rule-input`}
          data-testid={`${idPrefix}-add-rule-input`}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder={
            agentKind === 'repair'
              ? 'e.g. Ask for a photo first'
              : 'e.g. Ask for their move-in date'
          }
          className="min-h-[44px] min-w-0 flex-1 basis-60"
        />
        <Button type="submit" variant="outline" className="font-semibold" data-testid={`${idPrefix}-add-rule-button`}>
          Add rule
        </Button>
      </form>
      <span className="text-sm text-muted-foreground">{HELPER_TEXT[agentKind]}</span>
    </section>
  );
}

export default PlaybookRulesEditor;
